import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    Plus, BarChart3, TrendingUp, Award, Bell, Menu, X, ChevronLeft, 
    LogOut, Home, BarChart2, Settings, HelpCircle, User, MessageSquare, PlugZap
} from 'lucide-react';

// ShadCN UI Components
import { Button } from './ui/button';

const CommonSidebar = ({ user, onLogout, sidebarCollapsed, setSidebarCollapsed, mobileMenuOpen, setMobileMenuOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Navigation items with their routes
    const navigationItems = [
        {
            name: 'Dashboard',
            icon: Home,
            path: '/dashboard',
            onClick: () => navigate('/dashboard')
        },
        {
            name: 'Analytics',
            icon: BarChart2,
            path: '/analytics',
            onClick: () => navigate('/analytics')
        },
        {
            name: 'Feedback',
            icon: MessageSquare,
            path: '/feedback',
            onClick: () => navigate('/feedback')
        },
        {
            name: 'Integrations',
            icon: PlugZap,
            path: '/broker-integrations',
            onClick: () => navigate('/broker-integrations')
        },
        {
            name: 'Settings',
            icon: Settings,
            path: '/settings',
            onClick: () => navigate('/settings')
        },
        {
            name: 'Help',
            icon: HelpCircle,
            path: '/help',
            onClick: () => navigate('/help')
        }
    ];

    // Check if a navigation item is active
    const isActive = (path) => {
        return location.pathname === path;
    };

    const handleNavigation = (item) => {
        item.onClick();
        // Close mobile menu on navigation
        if (window.innerWidth < 1024) {
            setMobileMenuOpen(false);
        }
    };

    return (
        <div style={{ 
            all: 'unset', 
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: '#111827',
            backgroundColor: 'transparent'
        }}>
            {/* Mobile Menu Toggle */}
            <Button
                variant="ghost"
                size="icon"
                className="lg:hidden fixed top-4 left-4 z-50 bg-white shadow-lg hover:bg-gray-50"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Mobile Backdrop */}
            {mobileMenuOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                ${sidebarCollapsed ? 'w-20' : 'w-72'} 
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                fixed inset-y-0 left-0 z-50 
                bg-slate-900
                text-slate-100 
                transition-all duration-300 ease-in-out
                shadow-2xl backdrop-blur-xl
                flex flex-col
                h-screen
                border-r border-slate-700
            `}>
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-700 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {sidebarCollapsed ? (
                            <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
                                <BarChart3 className="w-5 h-5 text-white" />
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <span className="text-xl font-bold text-white tracking-wide">STOCKKHATA</span>
                            </div>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="hidden lg:flex text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-all duration-200"
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    >
                        <ChevronLeft className={`h-4 w-4 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
                    </Button>
                </div>

                {/* Profile Section */}
                <div className="flex items-center gap-3 p-6 border-b border-slate-700 flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center font-bold text-white shadow-lg ring-2 ring-blue-500/30">
                        {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    {!sidebarCollapsed && (
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
                            {user?.premium && (
                                <div className="text-xs text-blue-300 font-medium flex items-center gap-1">
                                    ✨ Premium Member
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    {navigationItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        
                        return (
                            <Button
                                key={item.name}
                                variant="ghost"
                                className={`w-full transition-all duration-200 rounded-xl ${
                                    sidebarCollapsed ? 'justify-center p-3' : 'justify-start px-4 py-3'
                                } ${
                                    active 
                                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg ring-2 ring-blue-500/30' 
                                        : 'text-slate-300 hover:text-white hover:bg-slate-800 hover:shadow-md'
                                }`}
                                onClick={() => handleNavigation(item)}
                                title={sidebarCollapsed ? item.name : ''}
                            >
                                <Icon className={`h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'} ${
                                    active ? 'text-white' : 'text-slate-300'
                                }`} />
                                {!sidebarCollapsed && (
                                    <span className={`${active ? 'font-semibold' : 'font-medium'} truncate`}>
                                        {item.name}
                                    </span>
                                )}
                            </Button>
                        );
                    })}
                </nav>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 flex-shrink-0">
                    <Button
                        variant="ghost"
                        className={`w-full transition-all duration-200 rounded-xl ${
                            sidebarCollapsed ? 'justify-center p-3' : 'justify-start px-4 py-3'
                        } text-red-400 hover:text-red-300 hover:bg-red-900/30 hover:shadow-md`}
                        onClick={onLogout}
                        title={sidebarCollapsed ? 'Logout' : ''}
                    >
                        <LogOut className={`h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                        {!sidebarCollapsed && <span className="font-medium">Logout</span>}
                    </Button>
                </div>
            </aside>
        </div>
    );
};

export default CommonSidebar;
